import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import {
    createEmailWalletUser,
    ensureEmailWalletVerified,
    getEmailWalletRecord,
    saveEmailWalletRecord,
    signInEmailWalletUser,
} from "../utils/emailWalletAuth";
import {
    createEncryptedEmailWallet,
    decryptRecoveryPhrase,
    importRecoveryPhraseAsEncryptedWallet,
    unlockEncryptedEmailWallet,
} from "../utils/nativeEmailWalletCrypto";
import {
    NATIVE_EMAIL_WALLET_NAME,
    nativeEmailWalletAdapter,
} from "../utils/nativeEmailWalletAdapter";
import "./EmailWalletButton.css";
import { trackUsageEvent, usageAttempt, usageFailureCode } from '../utils/usageAnalytics';

const MIN_PASSWORD_LENGTH = 8;

export default function EmailWalletButton({ compact = false }) {
    const wallet = useWallet();
    const selectedAdapter = wallet.wallet?.adapter;
    const selectWallet = wallet.select;
    const walletRef = useRef(wallet);
    walletRef.current = wallet;
    const operationRef = useRef(false);
    const [busy, setBusy] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [importPhrase, setImportPhrase] = useState("");
    const [recoveryPhrase, setRecoveryPhrase] = useState("");
    const [recoveryPublicKey, setRecoveryPublicKey] = useState("");
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [pendingAuth, setPendingAuth] = useState(null);
    const [verificationEmail, setVerificationEmail] = useState("");

    useEffect(() => {
        if (!pendingAuth) return;
        if (selectedAdapter !== nativeEmailWalletAdapter) {
            setStatus("Connecting email wallet...");
            selectWallet(NATIVE_EMAIL_WALLET_NAME);
            return;
        }
        if (!wallet.connected || !nativeEmailWalletAdapter.connected ||
            wallet.publicKey?.toBase58?.() !== pendingAuth.publicKey) {
            setStatus("Opening MuultiSpace session...");
            return;
        }
        setStatus("Email wallet connected: " + pendingAuth.publicKey.slice(0, 4) + "..." + pendingAuth.publicKey.slice(-4));
        if (pendingAuth.usageAttempt) void trackUsageEvent('email_wallet_connected', pendingAuth.usageAttempt);
        setPendingAuth(null);
        setPassword("");
        setConfirmPassword("");
        setImportPhrase("");
        if (!pendingAuth.keepOpen) {
            setIsOpen(false);
            setRecoveryPhrase("");
            setRecoveryPublicKey("");
        }
    }, [pendingAuth, selectedAdapter, selectWallet, wallet.connected, wallet.publicKey]);

    useEffect(() => {
        if (!pendingAuth) return undefined;
        const handleError = (err) => {
            if (pendingAuth.usageAttempt) void trackUsageEvent('email_flow_failed', { ...pendingAuth.usageAttempt, failureCode: usageFailureCode(err) });
            setPendingAuth(null);
            setError(err?.message || "Email wallet connection failed.");
            setStatus("");
        };
        const handleDisconnect = () => {
            setPendingAuth(null);
            setStatus("");
        };
        nativeEmailWalletAdapter.on("error", handleError);
        nativeEmailWalletAdapter.on("disconnect", handleDisconnect);
        return () => {
            nativeEmailWalletAdapter.off("error", handleError);
            nativeEmailWalletAdapter.off("disconnect", handleDisconnect);
        };
    }, [pendingAuth]);

    const resetMessages = () => {
        setError("");
        setStatus("");
        setRecoveryPhrase("");
        setRecoveryPublicKey("");
        setVerificationEmail("");
    };

    const validatePassword = () => {
        if (password.length < MIN_PASSWORD_LENGTH) {
            throw Object.assign(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`), { usageCode: 'validation' });
        }
    };

    const attachWallet = async ({ keypair, publicKey }, options = {}) => {
        // Finish the previous adapter's disconnect before selecting email. Its
        // delayed disconnect event must not clear the new wallet selection.
        if (walletRef.current.wallet?.adapter) await walletRef.current.disconnect();
        nativeEmailWalletAdapter.setSession(keypair);
        setPendingAuth({ publicKey, keepOpen: !!options.keepOpen, usageAttempt: options.usageAttempt });
    };

    const requireVerifiedEmail = async (nextStep, options = {}) => {
        const verifiedUser = await ensureEmailWalletVerified({
            sendIfNeeded: options.sendIfNeeded !== false,
        });
        if (verifiedUser) {
            if (options.usageAttempt) void trackUsageEvent('email_verification_completed', options.usageAttempt);
            setVerificationEmail("");
            return verifiedUser;
        }

        const address = email.trim();
        if (options.usageAttempt) void trackUsageEvent('email_verification_pending', options.usageAttempt);
        setVerificationEmail(address);
        setStatus(
            `${options.alreadySent ? "A verification link was sent" : "Verification email sent"} to ${address}. ` +
            `Verify it, then ${nextStep}.`
        );
        return null;
    };

    const handleResendVerification = async () => {
        const attempt = usageAttempt('verification');
        setError("");
        setStatus("Sending verification email...");

        try {
            validatePassword();
            await signInEmailWalletUser(email, password);
            const verifiedUser = await requireVerifiedEmail("continue", { sendIfNeeded: true, usageAttempt: attempt });
            if (!verifiedUser) void trackUsageEvent('email_verification_resent', attempt);
            if (verifiedUser) {
                setStatus("Email verified. You can continue.");
            }
        } catch (err) {
            void trackUsageEvent('email_flow_failed', { ...attempt, failureCode: usageFailureCode(err) });
            console.error("Email verification resend failed:", err);
            setError(err?.message || "Could not send verification email.");
            setStatus("");
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        if (operationRef.current) return;
        operationRef.current = true;
        setBusy(true);
        resetMessages();

        const attempt = usageAttempt('login');
        let failureCode = 'other';
        void trackUsageEvent('email_login_attempted', attempt);

        try {
            validatePassword();
            setStatus("Checking email wallet...");
            await signInEmailWalletUser(email, password);
            void trackUsageEvent('email_auth_succeeded', attempt);
            const verifiedUser = await requireVerifiedEmail("log in again", { usageAttempt: attempt });
            if (!verifiedUser) return;

            const record = await getEmailWalletRecord();
            if (!record?.exists) {
                throw Object.assign(new Error("No Muulti email wallet exists for this email yet."), { usageCode: 'wallet_missing' });
            }

            failureCode = 'wallet_unlock';
            const unlocked = await unlockEncryptedEmailWallet(
                record.encryptedWallet,
                password,
                record.publicKey
            );
            void trackUsageEvent('email_wallet_unlocked', attempt);
            failureCode = 'other';
            await attachWallet(unlocked, { usageAttempt: attempt });
        } catch (err) {
            void trackUsageEvent('email_flow_failed', { ...attempt, failureCode: usageFailureCode(err, failureCode) });
            console.error("Email wallet login failed:", err);
            setError(err?.message || "Email wallet login failed.");
            setStatus("");
        } finally {
            operationRef.current = false;
            setBusy(false);
        }
    };

    const handleSignup = async (event) => {
        event.preventDefault();
        if (operationRef.current) return;
        operationRef.current = true;
        setBusy(true);
        resetMessages();

        const attempt = usageAttempt('signup');
        void trackUsageEvent('email_signup_attempted', attempt);

        try {
            validatePassword();
            if (password !== confirmPassword) {
                throw Object.assign(new Error("Passwords do not match."), { usageCode: 'validation' });
            }

            let createdEmailLogin = false;
            setStatus("Creating email login...");
            try {
                await createEmailWalletUser(email, password);
                createdEmailLogin = true;
            } catch (err) {
                if (err?.code !== "auth/email-already-in-use") {
                    throw err;
                }

                setStatus("Email login exists. Checking verification...");
                await signInEmailWalletUser(email, password);
            }
            void trackUsageEvent('email_auth_succeeded', attempt);

            const verifiedUser = await requireVerifiedEmail("click Create Email Wallet again", {
                sendIfNeeded: !createdEmailLogin,
                alreadySent: createdEmailLogin,
                usageAttempt: attempt,
            });
            if (!verifiedUser) {
                return;
            }

            const existingRecord = await getEmailWalletRecord();
            if (existingRecord?.exists) {
                throw Object.assign(new Error("This email already has a Muulti wallet. Use Log in instead."), { usageCode: 'wallet_exists' });
            }

            setStatus("Creating encrypted wallet...");
            const walletPayload = importPhrase.trim()
                ? await importRecoveryPhraseAsEncryptedWallet(importPhrase, password)
                : await createEncryptedEmailWallet(password);

            await saveEmailWalletRecord({
                publicKey: walletPayload.publicKey,
                encryptedWallet: walletPayload.encryptedWallet,
            });
            void trackUsageEvent('email_wallet_created', attempt);

            setRecoveryPhrase(walletPayload.mnemonic || importPhrase.trim());
            setRecoveryPublicKey(walletPayload.publicKey);
            await attachWallet(walletPayload, { keepOpen: true, usageAttempt: attempt });
        } catch (err) {
            void trackUsageEvent('email_flow_failed', { ...attempt, failureCode: usageFailureCode(err) });
            console.error("Email wallet signup failed:", err);
            setError(err?.message || "Email wallet signup failed.");
            setStatus("");
        } finally {
            operationRef.current = false;
            setBusy(false);
        }
    };

    const handleRevealRecoveryPhrase = async (event) => {
        event.preventDefault();
        if (operationRef.current) return;
        operationRef.current = true;
        setBusy(true);
        resetMessages();

        try {
            validatePassword();
            setStatus("Loading encrypted wallet...");
            await signInEmailWalletUser(email, password);
            const verifiedUser = await requireVerifiedEmail("try Recovery again");
            if (!verifiedUser) return;

            const record = await getEmailWalletRecord();
            if (!record?.exists) {
                throw new Error("No Muulti email wallet exists for this email.");
            }
            const phrase = await decryptRecoveryPhrase(record.encryptedWallet, password);
            setRecoveryPhrase(phrase);
            setRecoveryPublicKey(record.publicKey || "");
            setStatus("Recovery phrase decrypted locally.");
        } catch (err) {
            console.error("Recovery phrase reveal failed:", err);
            setError(err?.message || "Could not reveal recovery phrase.");
            setStatus("");
        } finally {
            operationRef.current = false;
            setBusy(false);
        }
    };

    const openModal = () => {
        void trackUsageEvent('email_wallet_opened', { flow: 'login' });
        resetMessages();
        setMode("login");
        setIsOpen(true);
    };

    const modal = isOpen ? (
        <div className="email-wallet-modal" role="dialog" aria-modal="true" aria-label="Email wallet">
            <div className="email-wallet-modal__backdrop" onClick={() => setIsOpen(false)} />
            <div className="email-wallet-modal__panel">
                <div className="email-wallet-modal__header">
                    <h2>Email Wallet</h2>
                    <button type="button" className="email-wallet-modal__close" onClick={() => setIsOpen(false)}>
                        x
                    </button>
                </div>

                <div className="email-wallet-tabs" role="tablist" aria-label="Email wallet mode">
                    <button
                        type="button"
                        className={mode === "login" ? "is-active" : ""}
                        onClick={() => { resetMessages(); setMode("login"); void trackUsageEvent('email_login_selected', { flow: 'login' }); }}
                    >
                        Log in
                    </button>
                    <button
                        type="button"
                        className={mode === "signup" ? "is-active" : ""}
                        onClick={() => { resetMessages(); setMode("signup"); void trackUsageEvent('email_signup_selected', { flow: 'signup' }); }}
                    >
                        Sign up
                    </button>
                    <button
                        type="button"
                        className={mode === "recovery" ? "is-active" : ""}
                        onClick={() => { resetMessages(); setMode("recovery"); }}
                    >
                        Recovery
                    </button>
                </div>

                <form
                    className="email-wallet-form"
                    onSubmit={
                        mode === "signup"
                            ? handleSignup
                            : mode === "recovery"
                            ? handleRevealRecoveryPhrase
                            : handleLogin
                    }
                >
                    <label>
                        Email
                        <input
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                        />
                    </label>

                    <label>
                        Password
                        <input
                            type="password"
                            autoComplete={mode === "signup" ? "new-password" : "current-password"}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            minLength={MIN_PASSWORD_LENGTH}
                            required
                        />
                    </label>

                    {mode === "signup" ? (
                        <>
                            <label>
                                Confirm password
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    minLength={MIN_PASSWORD_LENGTH}
                                    required
                                />
                            </label>

                            <label>
                                Existing recovery phrase (optional)
                                <textarea
                                    value={importPhrase}
                                    onChange={(event) => setImportPhrase(event.target.value)}
                                    rows={3}
                                    placeholder="Leave blank to create a new wallet"
                                />
                            </label>
                        </>
                    ) : null}

                    <button type="submit" className="email-wallet-submit" disabled={busy || !!pendingAuth}>
                        {mode === "signup"
                            ? "Create Email Wallet"
                            : mode === "recovery"
                            ? "Reveal Recovery Phrase"
                            : "Log In With Email"}
                    </button>
                </form>

                {status ? <div className="email-wallet-status">{status}</div> : null}
                {error ? <div className="email-wallet-error">{error}</div> : null}

                {verificationEmail ? (
                    <div className="email-wallet-verification">
                        <div>Waiting on verification for {verificationEmail}.</div>
                        <button type="button" onClick={handleResendVerification}>
                            Resend verification email
                        </button>
                    </div>
                ) : null}

                {recoveryPhrase ? (
                    <div className="email-wallet-recovery">
                        <div className="email-wallet-recovery__title">Recovery phrase</div>
                        {recoveryPublicKey ? (
                            <div className="email-wallet-recovery__address">
                                <span>Email wallet address</span>
                                <code>{recoveryPublicKey}</code>
                            </div>
                        ) : null}
                        <div className="email-wallet-recovery__body">
                            <strong>Save this recovery phrase somewhere private.</strong>
                            <p>
                                Your email and password unlock this Solana wallet in MuultiSpace, but this
                                phrase is the wallet backup. If you forget the password that encrypted this
                                wallet, a normal email password reset may not recover the wallet without this
                                phrase.
                            </p>
                            <p>
                                This email wallet is separate from Phantom, Solflare, or any other wallet.
                                Token gates, SOL payments, mints, and asset checks use this address. Assets
                                in another wallet will not count here unless they are moved or you import the
                                same recovery phrase there.
                            </p>
                        </div>
                        <textarea readOnly rows={4} value={recoveryPhrase} />
                    </div>
                ) : null}

                <p className="email-wallet-note">
                    Verify your email before wallet setup or unlock. The wallet decrypts in your browser.
                </p>
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                type="button"
                className={`email-wallet-trigger ${compact ? "email-wallet-trigger--compact" : ""}`}
                onClick={openModal}
            >
                Email Wallet
            </button>

            {modal && typeof document !== "undefined" ? createPortal(modal, document.body) : modal}
        </>
    );
}
